import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Heart, ListPlus, MoreHorizontal, Pencil, Radio, Shuffle, Sparkles, Trash2 } from 'lucide-react';
import { useAccess } from '../lib/access';
import type { Album, Track } from '@pujolisique/shared';
import { useAlbum, useArtist, useComposer, useFavoriteToggle, useGenre, usePersonInfo, usePlaylist, usePlaylistMutations } from '../api/hooks';
import { AlbumCard, Grid, PersonCard, Shelf } from '../components/Cards';
import { Cover } from '../components/Cover';
import { PageHeader, PlayButton } from '../components/PageHeader';
import { Empty, ErrorBox, Loading } from '../components/States';
import { TrackList } from '../components/TrackList';
import { DeleteButton, useAlbumMenu, useStartMix } from '../components/trackActions';
import { describeRules } from './smartRules';
import { fmtLong, isHiRes, plural, qualityLabel } from '../lib/format';
import { usePlayer } from '../player/store';
import { useUi } from '../ui/store';
import { toast } from '../ui/toast';
import { useNavigate } from 'react-router-dom';

export function Bio({ text, url, source }: { text?: string; url?: string; source?: string }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > 280;
  return (
    <div className="bio">
      <p className={`bio__text ${long && !open ? 'is-clamped' : ''}`}>{text}</p>
      <div className="bio__links">
        {long && (
          <button className="link-quiet" onClick={() => setOpen((v) => !v)}>
            {open ? 'Réduire' : 'Lire la suite'}
          </button>
        )}
        {url && (
          <a className="link-quiet" href={url} target="_blank" rel="noreferrer">
            Source : {source ?? 'web'}
          </a>
        )}
      </div>
    </div>
  );
}

/* ================================ Album ================================ */
export function AlbumPage() {
  const { id = '' } = useParams();
  const q = useAlbum(id);
  const playTracks = usePlayer((s) => s.playTracks);
  const pickPlaylist = useUi((s) => s.pickPlaylist);
  const fav = useFavoriteToggle();
  const menu = useAlbumMenu();
  const mix = useStartMix();
  const nav = useNavigate();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const a = q.data;
  const hires = a.tracks.some(isHiRes);
  const quality = a.tracks[0] ? qualityLabel(a.tracks[0]) : undefined;
  return (
    <div className="page page--detail">
      <PageHeader
        kind="Album"
        title={a.name}
        art={`album/${a.id}`}
        editPath={`/edit/album/${a.id}`}
        trailing={<DeleteButton scope={{ album: a.id }} label={`l’album « ${a.name} »`} onDone={() => nav('/albums', { replace: true })} />}
        meta={
          <>
            {a.artists.map((ar, i) => (
              <span key={ar.id || ar.name}>
                {i > 0 && ', '}
                {ar.id ? <Link to={`/artist/${ar.id}`} className="strong">{ar.name}</Link> : <span className="strong">{ar.name}</span>}
              </span>
            ))}
            {a.year && <span>{a.year}</span>}
            <span>{plural(a.tracks.length, 'titre', 'titres')}</span>
            <span>{fmtLong(a.duration)}</span>
            {quality && <span className={`badge ${hires ? 'badge--hires' : ''}`}>{hires ? 'Hi-Res ' : ''}{quality}</span>}
          </>
        }
        actions={
          <>
            <PlayButton big onClick={() => playTracks(a.tracks, 0, { source: a.name, shuffle: false })} label={`Lire ${a.name}`} />
            <button className="icon-btn icon-btn--big" onClick={() => playTracks(a.tracks, 0, { source: a.name, shuffle: true })} aria-label="Lecture aléatoire">
              <Shuffle size={22} />
            </button>
            <button className={`icon-btn icon-btn--big fav ${a.favorite ? 'is-on' : ''}`} onClick={() => fav.mutate({ kind: 'album', id: a.id, value: !a.favorite })} aria-pressed={!!a.favorite} aria-label={a.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}>
              <Heart size={22} fill={a.favorite ? 'currentColor' : 'none'} />
            </button>
            <button className="icon-btn icon-btn--big" onClick={() => pickPlaylist(a.tracks)} aria-label="Ajouter à une playlist">
              <ListPlus size={22} />
            </button>
            <button className="icon-btn icon-btn--big" onClick={() => mix('album', a.id, a.name)} aria-label="Lancer un mix">
              <Radio size={22} />
            </button>
            <button className="icon-btn icon-btn--big" onClick={(e) => menu(a, e.currentTarget.getBoundingClientRect())} aria-label="Plus d’actions">
              <MoreHorizontal size={22} />
            </button>
          </>
        }
      >
        {a.composers.length > 0 && (
          <p className="hero__composers">
            Compositeurs :{' '}
            {a.composers.map((c, i) => (
              <span key={c.id}>
                {i > 0 && ', '}
                {c.id.startsWith('name:') ? c.name : <Link to={`/composer/${c.id}`}>{c.name}</Link>}
              </span>
            ))}
          </p>
        )}
      </PageHeader>
      <TrackList tracks={a.tracks} source={a.name} numbering="track" groupDiscs />
      {(a.genres.length > 0 || (a.themes?.length ?? 0) > 0) && (
        <p className="foot-meta">
          {a.genres.map((g) => (
            <Link key={g} to={`/genre/${encodeURIComponent(g)}`} className="chip">
              {g}
            </Link>
          ))}
          {a.themes?.map((t) => (
            <Link key={`t-${t}`} to={`/search?q=${encodeURIComponent(t)}`} className="chip chip--theme" title="Thème">
              # {t}
            </Link>
          ))}
        </p>
      )}
      {a.description && <Bio text={a.description} />}
    </div>
  );
}

/* =============================== Artiste =============================== */
export function ArtistPage() {
  const { id = '' } = useParams();
  const q = useArtist(id);
  const info = usePersonInfo('artist', id, q.data && !q.data.bio ? q.data.name : undefined);
  const playTracks = usePlayer((s) => s.playTracks);
  const fav = useFavoriteToggle();
  const mix = useStartMix();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const a = q.data;
  const bio = a.bio ?? info.data?.info?.summary;
  return (
    <div className="page page--detail">
      <PageHeader
        kind="Artiste"
        title={a.name}
        art={`artist/${a.id}`}
        editPath={`/edit/person/${a.id}`}
        round
        meta={<span>{plural(a.albums.length, 'album', 'albums')}</span>}
        actions={
          <>
            <PlayButton big onClick={() => a.topTracks.length && playTracks(a.topTracks, 0, { source: a.name })} label={`Lire ${a.name}`} />
            <button className="btn btn--ghost" onClick={() => mix('artist', a.id, a.name)}>
              <Radio size={17} /> Mix
            </button>
            <button className={`icon-btn icon-btn--big fav ${a.favorite ? 'is-on' : ''}`} onClick={() => fav.mutate({ kind: 'artist', id: a.id, value: !a.favorite })} aria-pressed={!!a.favorite} aria-label={a.favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}>
              <Heart size={22} fill={a.favorite ? 'currentColor' : 'none'} />
            </button>
          </>
        }
      />
      {a.topTracks.length > 0 && (
        <section className="section">
          <h2 className="section__title">Titres populaires</h2>
          <TrackList tracks={a.topTracks.slice(0, 10)} source={a.name} showCover showComposer={false} />
        </section>
      )}
      {a.albums.length > 0 && (
        <section className="section">
          <h2 className="section__title">Discographie</h2>
          <Grid>
            {a.albums.map((al) => (
              <AlbumCard key={al.id} album={al} sub={al.year ? String(al.year) : 'Album'} />
            ))}
          </Grid>
        </section>
      )}
      {a.appearsOn.length > 0 && (
        <Shelf title="Apparaît aussi sur">
          {a.appearsOn.map((al) => (
            <AlbumCard key={al.id} album={al} />
          ))}
        </Shelf>
      )}
      {a.similar && a.similar.length > 0 && (
        <Shelf title="Artistes proches">
          {a.similar.map((s) => (
            <PersonCard key={s.id} person={s} kind="artist" />
          ))}
        </Shelf>
      )}
      {bio && (
        <section className="section">
          <h2 className="section__title">À propos</h2>
          <Bio text={bio} url={a.bio ? undefined : info.data?.info?.url} source={info.data?.info?.source} />
        </section>
      )}
    </div>
  );
}

/* ============================= Compositeur ============================= */
export function ComposerPage() {
  const { id = '' } = useParams();
  const q = useComposer(id);
  const info = usePersonInfo('composer', id, q.data?.name);
  const playTracks = usePlayer((s) => s.playTracks);
  const mix = useStartMix();
  const [view, setView] = useState<'albums' | 'tracks'>('albums');

  const groups = useMemo(() => {
    if (!q.data) return [];
    const byAlbum = new Map<string, Track[]>();
    for (const t of q.data.tracks) {
      const k = t.albumId ?? `?${t.album}`;
      byAlbum.set(k, [...(byAlbum.get(k) ?? []), t]);
    }
    const albums = new Map(q.data.albums.map((a) => [a.id, a]));
    return [...byAlbum.entries()].map(([k, tracks]) => ({ album: albums.get(k), key: k, tracks }));
  }, [q.data]);

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const c = q.data;
  const i = info.data?.info;
  return (
    <div className="page page--detail">
      <PageHeader
        kind="Compositeur"
        title={c.name}
        art={`composer/${c.id}`}
        editPath={`/edit/person/${c.id}`}
        round
        meta={
          <>
            {i?.description && <span className="strong">{i.description}</span>}
            <span>{plural(c.tracks.length, 'œuvre', 'œuvres')}</span>
            <span>{plural(c.albums.length, 'enregistrement', 'enregistrements')}</span>
          </>
        }
        actions={
          <>
            <PlayButton big onClick={() => playTracks(c.tracks, 0, { source: c.name, shuffle: false })} label={`Lire les œuvres de ${c.name}`} />
            <button className="icon-btn icon-btn--big" onClick={() => playTracks(c.tracks, 0, { source: c.name, shuffle: true })} aria-label="Lecture aléatoire">
              <Shuffle size={22} />
            </button>
            <button className="btn btn--ghost" onClick={() => mix('composer', c.id, c.name)}>
              <Radio size={17} /> Mix {c.name.split(' ').pop()}
            </button>
          </>
        }
      >
        {i?.summary && <Bio text={i.summary} url={i.url} source={i.source} />}
      </PageHeader>

      <div className="page__bar page__bar--tight">
        <h2 className="section__title">Œuvres</h2>
        <div className="seg" role="radiogroup" aria-label="Affichage">
          <button role="radio" aria-checked={view === 'albums'} className={view === 'albums' ? 'is-on' : ''} onClick={() => setView('albums')}>
            Par enregistrement
          </button>
          <button role="radio" aria-checked={view === 'tracks'} className={view === 'tracks' ? 'is-on' : ''} onClick={() => setView('tracks')}>
            Tous les titres
          </button>
        </div>
      </div>

      {!c.tracks.length && <Empty title="Aucune œuvre trouvée" />}
      {view === 'tracks' && <TrackList tracks={c.tracks} source={c.name} showAlbum showComposer={false} showCover />}
      {view === 'albums' &&
        groups.map((g) => <ComposerGroup key={g.key} album={g.album} tracks={g.tracks} composer={c.name} />)}
    </div>
  );
}

function ComposerGroup({ album, tracks, composer }: { album?: Album; tracks: Track[]; composer: string }) {
  const playTracks = usePlayer((s) => s.playTracks);
  const name = album?.name ?? tracks[0]?.album ?? 'Album inconnu';
  const performers = album?.artists.map((a) => a.name).join(', ') ?? tracks[0]?.albumArtist;
  return (
    <section className="cgroup">
      <header className="cgroup__head">
        <Link to={album ? `/album/${album.id}` : '#'} className="cgroup__art">
          <Cover art={tracks[0]?.cover} name={name} size={128} />
        </Link>
        <div className="cgroup__text">
          {album ? <Link to={`/album/${album.id}`} className="cgroup__title">{name}</Link> : <span className="cgroup__title">{name}</span>}
          <span className="cgroup__sub">
            {[performers, album?.year].filter(Boolean).join(', ')}
          </span>
        </div>
        <PlayButton onClick={() => playTracks(tracks, 0, { source: `${composer} : ${name}` })} label={`Lire ${name}`} />
      </header>
      <TrackList tracks={tracks} source={`${composer} : ${name}`} numbering="track" showComposer={false} />
    </section>
  );
}

/* ================================ Genre ================================ */
export function GenrePage() {
  const { id = '' } = useParams();
  const q = useGenre(id);
  const playTracks = usePlayer((s) => s.playTracks);
  const mix = useStartMix();
  return (
    <div className="page">
      <div className="page__bar">
        <h1 className="page__title">{id}</h1>
        <div className="row-gap">
          <button className="btn btn--gold" disabled={!q.data?.tracks.length} onClick={() => q.data && playTracks(q.data.tracks, 0, { source: id })}>
            <Shuffle size={16} /> Écouter au hasard
          </button>
          <button className="btn btn--ghost" onClick={() => mix('genre', id, id)}>
            <Radio size={16} /> Mix
          </button>
        </div>
      </div>
      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.data && (
        <>
          <Grid>
            {q.data.albums.map((a) => (
              <AlbumCard key={a.id} album={a} />
            ))}
          </Grid>
          {q.data.tracks.length > 0 && (
            <section className="section">
              <h2 className="section__title">Quelques titres</h2>
              <TrackList tracks={q.data.tracks.slice(0, 20)} source={id} showCover showAlbum />
            </section>
          )}
        </>
      )}
    </div>
  );
}

/* =============================== Playlist ============================== */
export function PlaylistPage() {
  const { id = '' } = useParams();
  const q = usePlaylist(id);
  const nav = useNavigate();
  const playTracks = usePlayer((s) => s.playTracks);
  const { remove, rename, del } = usePlaylistMutations();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const { libraries } = useAccess();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const p = q.data;
  return (
    <div className="page page--detail">
      <PageHeader
        kind={p.smart ? 'Playlist intelligente' : 'Playlist'}
        title={p.name}
        art={p.tracks[0]?.cover}
        meta={
          <>
            {p.owner && <span className="strong">{p.owner}</span>}
            <span>{plural(p.tracks.length, 'titre', 'titres')}</span>
            <span>{fmtLong(p.duration)}</span>
          </>
        }
        actions={
          <>
            <PlayButton big onClick={() => p.tracks.length && playTracks(p.tracks, 0, { source: p.name, shuffle: false })} label={`Lire ${p.name}`} />
            <button className="icon-btn icon-btn--big" onClick={() => p.tracks.length && playTracks(p.tracks, 0, { source: p.name, shuffle: true })} aria-label="Lecture aléatoire">
              <Shuffle size={22} />
            </button>
            {p.canEdit && p.smart && (
              <Link className="btn btn--ghost" to={`/playlist/${p.id}/rules`}>
                <Sparkles size={16} /> Modifier les règles
              </Link>
            )}
            {p.canEdit && (
              <>
                <button className="icon-btn icon-btn--big" onClick={() => (setName(p.name), setEditing(true))} aria-label="Renommer">
                  <Pencil size={20} />
                </button>
                <button
                  className="icon-btn icon-btn--big"
                  aria-label="Supprimer la playlist"
                  onClick={() => {
                    if (!confirm(`Supprimer la playlist « ${p.name} » ?`)) return;
                    del.mutate(p.id, { onSuccess: () => (toast('Playlist supprimée'), nav('/playlists')), onError: (e) => toast(e.message, 'error') });
                  }}
                >
                  <Trash2 size={20} />
                </button>
              </>
            )}
          </>
        }
      >
        {p.rules && <p className="hero__composers smart__summary">{describeRules(p.rules, (id) => libraries.find((l) => l.id === id)?.name ?? id)}</p>}
        {editing && (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              rename.mutate({ id: p.id, name }, { onSuccess: () => (setEditing(false), toast('Playlist renommée')), onError: (err) => toast(err.message, 'error') });
            }}
          >
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus aria-label="Nouveau nom" />
            <button className="btn btn--gold" type="submit">
              Renommer
            </button>
            <button className="btn btn--ghost" type="button" onClick={() => setEditing(false)}>
              Annuler
            </button>
          </form>
        )}
      </PageHeader>
      {!p.tracks.length &&
        (p.smart ? (
          <Empty title="Aucun titre ne correspond pour l’instant">Les titres apparaîtront d’eux-mêmes quand ils rempliront les règles.</Empty>
        ) : (
          <Empty title="Cette playlist est vide">Ajoutez des titres avec le menu « … » d’un morceau ou d’un album.</Empty>
        ))}
      <TrackList
        tracks={p.tracks}
        source={p.name}
        showCover
        showAlbum
        onRemove={
          p.canEdit && !p.smart
            ? (t) =>
                t.entryId &&
                remove.mutate(
                  { id: p.id, entryIds: [t.entryId] },
                  {
                    onSuccess: (r) => {
                      toast('Titre retiré');
                      // Jellyfin < 10.11 : la playlist a été recréée sous un nouvel identifiant
                      if (r.id && r.id !== p.id) nav(`/playlist/${r.id}`, { replace: true });
                    },
                    onError: (e) => toast(e.message, 'error'),
                  },
                )
            : undefined
        }
      />
    </div>
  );
}
